<template>
  <div class="app-tariff-options-wrapper">
    <div v-if="getView === 'viewOptions'">
      <div class="main-options">
        <OptionsBoxList :options="mainOptions" @next="next">
          <template v-for="slot in possibleChildSlotsFor('Horizontal')" v-slot:[`${slot.child}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>
        </OptionsBoxList>
      </div>
      <div class="sub-options">
        <OptionsBoxList :options="subOptions"
                        @next="next">
          <template v-for="slot in possibleChildSlotsFor('Vertical')" v-slot:[`${slot.child}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>
        </OptionsBoxList>
      </div>
      <div class="sub-options" v-if="fullBookableServiceoptions">
        <OptionsBoxList :options="fullBookableServiceoptions"
                        @next="next">
          <template v-for="slot in possibleChildSlotsFor('Vertical')" v-slot:[`${slot.child}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>
        </OptionsBoxList>
      </div>
    </div>
    <OptionDetail v-if="getView === 'detail'"
                  prev-step="viewOptions"
                  :option="selectedOption_.option"
                  :type="selectedOption_.type">
      <template v-for="slot in possibleChildSlotsFor('Detail', selectedOption_.option.id)"
                v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </OptionDetail>
    <Success v-if="getView === 'success'" @prev="prev" :option="selectedOption_">
      <template v-for="slot in possibleChildSlotsFor('Success')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </Success>
  </div>
</template>

<script>
import { wrapQuery } from '@/helper/wrapGQLQuery'
import gql from 'graphql-tag'
import slotProxy from '@/mixins/slotProxy'
import extraData from '@/mixins/extraData'
import transaction from '@/mixins/transaction'
import OptionsBoxList from './OptionsBoxList.vue'
import joinReverseTariffOptionsExtra from '@/helper/joinReverseTariffOptionsExtras'
import OptionDetail from './Detail.vue'
import Success from './Success.vue'
import { mapGetters, mapMutations, mapActions } from 'vuex'

export default {
  name: 'TariffOptionsWrapper',
  components: { Success, OptionDetail, OptionsBoxList },
  data () {
    return {
      allOptions_: null,
      selectedOption: null,
      activeTariffoptionId: '',
      tariffoptions: {
        bookedTariffoptions: {
          bookedTariffoptions: []
        },
        bookableTariffoptions: {
          bookableTariffoptions: []
        }
      },
      serviceOptions: {
        anonymousCallRejection: null
      }
    }
  },
  created () {
    this.changeStep('viewOptions')
  },
  computed: {
    ...mapGetters('bookTariffOptions', [
      'getView'
    ]),
    selectedOption_: function () {
      return this.selectedOption
    },
    fullBookedTariffoptions: function () {
      return this.tariffoptions.bookedTariffoptions.bookedTariffoptions
    },
    fullBookableTariffoptions: function () {
      return this.tariffoptions.bookableTariffoptions.bookableTariffoptions
    },
    allOptions: function () {
      const extra = this.extraData
      if (this.options) {
        return extra.map(extra => {
          return (joinReverseTariffOptionsExtra(extra, this.options))
        })
      }
      return {}
    },
    options: function () {
      return [
        ...this.fullBookableTariffoptions,
        ...this.fullBookedTariffoptions
      ]
    },
    mainOptions: function () {
      return this.allOptions.filter(item => item.optionType === 'main')
    },
    subOptions: function () {
      return this.allOptions.filter(item => item.optionType === 'sub')
    },
    fullBookableServiceoptions: function () {
      const comp = this
      return this.extraData.filter(item => item.id === 'so_acr').map(service => {
        return {
          ...service,
          serviceOption: comp.serviceOptions,
          option: undefined,
          type: 'service'
        }
      })
    }
  },
  methods: {
    ...mapMutations('bookTariffOptions', [
      'changeStep'
    ]),
    ...mapActions('bookTariffOptions', [
      'bookTariffoption',
      'initiateCancelTariffOption',
      'updateServiceOption'
    ]),
    next (option) {
      const comp = this
      const opIndex = this.extraData.findIndex(op => op.id === option.option.id)
      if (option.option?.type !== 'service') {
        if (option.type === 'book') {
          // adding a new property is not automatically registed by vue
          // so we need to add it via $set to be recognized an computed properties
          // are re-evaluated
          this.$set(this.extraData[opIndex], 'loading', true)
          this.bookTariffoption({ tariffoptionId: option.option.id })
            .then((response) => {
              this.extraData[opIndex].loading = false
              if (response.success === true) {
                option.option.response = response
                // with initiate we must wait for it to be finished
                comp.selectedOption = option
                comp.changeStep('detail')
              } else {
                option.option.response = null
              }
            })
        } else if (option.type === 'cancel') {
          this.initiateCancelTariffOption({ tariffoptionId: option.option.id })
            .then((response) => {
              this.extraData[opIndex].loading = false
              if (response.success === true) {
                option.option.response = response
                // with initiate we must wait for it to be finished
                comp.selectedOption = option
                comp.changeStep('detail')
              } else {
                option.option.response = null
              }
            })
        }
      } else if (option.option?.type === 'service') {
        let status = true
        if (option.type === 'cancel') {
          status = false
        }
        this.loadingNext = true
        this.updateServiceOption(status)
        this.loadingNext = false
      }
    },
    prev () {
      if (this.selectedOption.option.type === 'service') {
        this.$apollo.queries.serviceOptions.refetch()
      } else {
        this.$apollo.queries.tariffoptions.refetch()
      }
      this.changeStep('viewOptions')
    }
  },
  apollo: {
    tariffoptions: function () {
      return wrapQuery(
        gql`query tariffOptions {
           tariffoptions {
            bookableTariffoptions {
              bookableTariffoptions {
                requiresContractSummary
                additionalInfo
                automaticExtension
                buttonText
                details
                formattedPrice
                name
                tariffoptionId
                name
                price
                conflictWithCurrent
                duration {
                  amount
                  unit
                }
                notBookableWith
              }
            }
            bookedTariffoptions {
              bookedTariffoptions {
                automaticExtension
                tariffoptionId
                name
                price
                duration {
                  amount
                  unit
                }
                statusKey
                state
                startOfRuntime
                endOfRuntime
                possibleChangingDate
                buttonText
                stateFlag
                cancelable
                formattedPrice
                restrictedService
                tariffState
              }
            }
           }
        }`, this, 'tariffoptions'
      )
    },
    serviceOptions: function () {
      return wrapQuery(
        gql`query serviceOptions {
          serviceOptions {
            anonymousCallRejection
          }
        }`, this, 'serviceOptions'
      )
    }
  },
  mixins: [
    slotProxy(),
    extraData(),
    transaction('tariffOptions'),
    transaction('serviceOptions')
  ]
}
</script>
